import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import { FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';

import * as Interfaces from 'app/interfaces';
import * as Steps from '..';
import { constants, logger } from 'app/utils';

import {
  validateOrRedeemBulkVouchers,
  validateOrRedeemIndividualVoucher
} from '../../../../services/gakService';
import Component from './Review';

type Values = Pick<Interfaces.Common.RedemptionFormValues, 'review'>;

const initialValues: Values = {
  review: {
    consentDeclared: false
  }
};
const initialErrors: FormikErrors<Values> = {};
const initialTouched: FormikTouched<Values> = {};

const tickValidator = () =>
  Yup.mixed<boolean>()
    .default(undefined)
    .test(
      'isTicked',
      "You can't continue until you confirm this.",
      function validate(value?: boolean) {
        return value;
      }
    );

const reviewSchema = Yup.object<Interfaces.Common.Review>().shape({
  consentDeclared: tickValidator()
});

const validationSchema = Yup.object<Values>().shape({
  review: reviewSchema
});

const Step: StepProps<Values> = {
  id: 'review',
  title: constants.RedemptionPage.title,
  stepName: 'Review',
  Component,
  validation: {
    schema: validationSchema,
    fieldNames: ['review.consentDeclared']
  },
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: (wizardContext, form) => {
          wizardContext.dispatch({
            type: 'GOTO_STEP',
            stepId: Steps.Redeem.Individual.Step.id,
            values: form.values
          });
        }
      },
      {
        content: 'Submit',
        shouldValidate: true,
        isSubmit: true,
        type: 'primary',
        onClick: async (context, form, appStates) => {
          const appContext = appStates[
            constants.RedemptionStorageKeys.State
          ] as Interfaces.Store.AppContext;

          try {
            form.setSubmitting(true);
            form.submitCount += 1;

            if (!appContext.currentState.idToken?.rawToken)
              throw new Error('ID token not set, authorised contact cannot be identified.');

            form.submitForm();

            const { values: _allValues } = form;
            const allValues = _allValues as Interfaces.Common.RedemptionFormValues;

            const validateOnly = false;

            if (allValues.redeem.redemptionType === Interfaces.Common.RedemptionType.Individual) {
              const { success } = await validateOrRedeemIndividualVoucher(
                allValues.redeem.individual,
                form,
                appStates,
                validateOnly
              );

              if (!success) {
                // Voucher should have been pre-validated at redemption step
                throw new Error('Failed to redeem individual voucher at review step');
              }
            } else if (allValues.redeem.redemptionType === Interfaces.Common.RedemptionType.Bulk) {
              const success = await validateOrRedeemBulkVouchers(
                allValues.redeem.bulk,
                form,
                appStates,
                validateOnly
              );

              if (!success) {
                // CSV should have been pre-validated at redemption step
                throw new Error('Failed to redeem bulk vouchers at review step');
              }
            }

            context.dispatch({
              type: 'GOTO_STEP',
              stepId: Steps.Done.Step.id,
              values: form.values
            });
          } catch (error: any) {
            logger.error('Transaction submission failed', error);
            context.dispatch({
              type: 'GOTO_STEP',
              stepId: Steps.Error.Step.id,
              values: form.values
            });
          }
          form.setSubmitting(false);
        }
      }
    ]
  }
};

export { Step, initialValues, initialTouched, initialErrors, reviewSchema, validationSchema };
